package nl.volleyplanner.web.controller.userprofile

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.dom.addClass
import kotlinx.dom.clear
import kotlinx.dom.removeClass
import kotlinx.html.FlowContent
import kotlinx.html.InputType
import kotlinx.html.a
import kotlinx.html.div
import kotlinx.html.dom.create
import kotlinx.html.fieldSet
import kotlinx.html.id
import kotlinx.html.input
import kotlinx.html.js.onClickFunction
import kotlinx.html.label
import kotlinx.html.p
import kotlinx.html.role
import kotlinx.html.span
import nl.volleyplanner.api.InviteOrganisationUserRequest
import nl.volleyplanner.api.InviteOrganisationUserResponse
import nl.volleyplanner.web.base.IconPosition
import nl.volleyplanner.web.base.component.BootstrapButton
import nl.volleyplanner.web.base.component.ButtonVariant
import nl.volleyplanner.web.base.component.ModalComponent
import nl.volleyplanner.web.base.component.ModalSize
import nl.volleyplanner.web.base.component.validatingForm
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.service.OrganisationContext
import nl.volleyplanner.web.service.WebUserAndOrganisationService
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement

class UserInvitedModal() : ModalComponent(
    "Gebruiker uitgenodigd",
    ModalSize.Large
) {
    override val footerButtons = listOf(
        BootstrapButton("Afsluiten", ButtonVariant.Secondary, null, mapOf("data-bs-dismiss" to "modal")),
    )

    private val stateFlow = MutableStateFlow<InviteOrganisationUserResponse?>(null)

    fun showModal(response: InviteOrganisationUserResponse) {
        stateFlow.value = response
        super.show()
    }

    override suspend fun getState() = stateFlow

    override fun FlowContent.renderBody() {
        val organisationId = WebUserAndOrganisationService.selectedOrganisationState.value!!.organisation.identifier
        val response = stateFlow.value
        if (response == null) {
            spinner()
        } else {
            p {
                span (classes = "fw-bolder") { +response.email }
                +" is aan de vereniging gekoppeld."
            }
            p (classes = "mb-0") {
                +"Stuur onderstaande link door zodat hij/zij een account kan aanmaken:"
            }
            p {
                val inviteUrl = "${window.location.origin}/invite/$organisationId?email=${response.email}&token=${response.token}"
                a (classes = "text-muted text-decoration-none small") {
                    onClickFunction = { event ->
                        event.preventDefault()
                        window.navigator.clipboard.writeText(inviteUrl)
                    }
                    +inviteUrl
                    icon("bi-clipboard", iconPosition = IconPosition.End, useMargin = true)
                }
            }
        }
    }
}

class InviteUserModal(
    val organisationContext: OrganisationContext,
    val invitedModal: UserInvitedModal,
    val onUserInvited: () -> Unit
) : ModalComponent(
    "Gebruiker uitnodigen"
) {
    override val footerButtons = listOf(
        BootstrapButton("Annuleren", ButtonVariant.Secondary, null, mapOf("data-bs-dismiss" to "modal")),
        BootstrapButton("Uitnodigen", ButtonVariant.Primary, {
            it.preventDefault()

            // TODO: kill me now. @see PersonEditModal
            val formElement = document.getElementById("vp-invite-user-form")!! as HTMLFormElement
            formElement.dispatchEvent(org.w3c.dom.events.Event("submit"))
        }
        )
    )

    override suspend fun getState(): StateFlow<*>? {
        return super.getState()
    }

    suspend fun save() {
        val emailElement = document.getElementById("vp-user-email")!! as HTMLInputElement
        val nameElement = document.getElementById("vp-user-name")!! as HTMLInputElement

        val request = InviteOrganisationUserRequest(
            email = emailElement.value,
            name = nameElement.value
        )
        val response = organisationContext.volleyplannerApiClient.organisation.admin.inviteUser(request)
        onUserInvited.invoke()
        hide()
        invitedModal.showModal(response)
    }

    override fun FlowContent.renderBody() {
        validatingForm(
            validSubmitFunction = { event, formElement ->
                event.preventDefault()

                val statusElement = document.getElementById("vp-invite-user-form-status")!! as HTMLElement

                statusElement.removeClass("d-none")
                statusElement.clear()

                statusElement.append(document.create.div (classes = "alert alert-secondary d-flex align-items-center") {
                    role = "alert"
                    spinner()
                    +"Gegevens verwerken..."
                })

                save()

                statusElement.addClass("d-none")
                hide()
            },
            onSubmitErrorFunction = { throwable, formElement ->
                val statusElement = document.getElementById("vp-invite-user-form-status")!! as HTMLElement
                statusElement.removeClass("d-none")
                statusElement.clear()

                statusElement.append(document.create.div (classes = "alert alert-danger d-flex align-items-center") {
                    role = "alert"
                    icon("bi-exclamation-triangle")
                    +"Fout bij opslaan gegevens: ${throwable.message}"
                })
            }
        ) {
            id = "vp-invite-user-form"

            div (classes = "d-none") {
                id = "vp-invite-user-form-status"
            }
            div (classes = "p-3 mb-2") {
                fieldSet {
                    div(classes = "row g-3 mb-4 align-items-center") {
                        label(classes = "col-12 col-sm-3 col-form-label") {
                            htmlFor = "vp-user-name"
                            +"Naam"
                        }
                        div(classes = "col-12 col-sm-9") {
                            input(type = InputType.text, classes = "form-control") {
                                id = "vp-user-name"
                                name = "vp-user-name"
                                placeholder = "Naam"
                                required = true
                            }
                            div(classes = "invalid-feedback") {
                                +"Een naam is verplicht."
                            }
                        }
                    }
                    div(classes = "row g-3 mb-4 align-items-center") {
                        label(classes = "col-12 col-sm-3 col-form-label") {
                            htmlFor = "vp-user-email"
                            +"Email"
                        }
                        div(classes = "col-12 col-sm-9") {
                            input(type = InputType.text, classes = "form-control") {
                                id = "vp-user-email"
                                name = "vp-user-email"
                                placeholder = "Email"
                                required = true
                            }
                            div(classes = "invalid-feedback") {
                                +"Een email adres is verplicht."
                            }
                        }
                    }
                }
            }
        }
    }

}